import React, { useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    fetchCustomerUsageRatePerResource,
    ServiceCenterCustomerUsageRateReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { CustomerUsageEntry } from "../../statisticsTypes"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { ResourceTypeFilter } from "../common/ResourceTypeFilter"
import { useExportCustomerUsage } from "../../hooks/useExportCustomerUsage"
import { CompaniesSelector } from "./CompaniesSelector"
import { AccountingCustomer } from "../../../../../merchant/accounting/accounting/accountingApi"
import { useDateTime } from "../../../../../localization/useDateTime"

const selectCustomerUsage = (data: ServiceCenterCustomerUsageRateReportResponse) => data?.usageRates || []

export function CustomerUsage() {
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const [selectedResource, setSelectedResource] = useState<ResourceType>(ResourceType.ROOM)
    const [customer, setCustomer] = useState<AccountingCustomer | null>(null)
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const tableInstanceRef = useRef<MRT_TableInstance<CustomerUsageEntry>>(null)

    const customerUsageResult = useApiQuery(fetchCustomerUsageRatePerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_CUSTOMER_USAGE_SUMMARY,
        dependencies: {
            companyId: customer?.id,
            serviceCenterId: serviceCenterId ?? 0,
            resourceType: selectedResource,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: selectCustomerUsage,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(customerUsageResult)

    const columns = useMemo<MRT_ColumnDef<CustomerUsageEntry>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-occupancy-table-resource-title"),
                accessorKey: "resource.displayName",
            }),

            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-customer-usage-table-usage-rate-title"),
                accessorKey: "usageRate",
            }),
        ],
        [l10n, columnFactory],
    )

    const exportBookingDurations = useExportCustomerUsage({
        data: customerUsageResult.data,
        startDate: dateState.startDate || today,
        endDate: dateState.endDate || today,
    })

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                initialState={{ density: "spacious" }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton
                            onClick={() => {
                                exportBookingDurations()
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <LocalizedStrict
                            id="service-center-reports-statistic-occupancy-table-date-range-filter"
                            attrs={{ label: true }}
                        >
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                        </LocalizedStrict>
                        <ResourceTypeFilter value={selectedResource} onChange={setSelectedResource} />
                        <CompaniesSelector companyItem={customer} handleChange={setCustomer} />
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
